import React from 'react';
import { FormHelperText } from '@mui/material';
import { InputHelperText, InputProps, Labeled, useInput, useTheme } from 'react-admin';
import ReactJson, { InteractionProps, ReactJsonViewProps } from 'react-json-view';

export const defaultOptions: Omit<ReactJsonViewProps, 'src'> = {
  collapsed: false,
  iconStyle: 'square',
  indentWidth: 4,
};

type Props = {
  jsonString?: boolean;
  reactJsonOptions?: Omit<ReactJsonViewProps, 'src'>;
} & InputProps;

export const JsonInput: React.FC<Props> = (props) => {
  const [theme] = useTheme();

  const {
    field: { value, onChange },
    fieldState: { isTouched, error },
    formState: { isSubmitted },
    isRequired,
  } = useInput(props);

  const { source, label, helperText, jsonString = false, reactJsonOptions } = props;
  const { style: customStyles, ...reactJsonOverrides } = reactJsonOptions ?? {};

  function change(updatedSrc: unknown) {
    let updatedValue = updatedSrc;

    if (jsonString) {
      updatedValue = updatedSrc == null || typeof updatedSrc !== 'object' ? null : JSON.stringify(updatedSrc);
    }

    onChange(updatedValue);
  }

  function onEdit(edit: InteractionProps) {
    change(edit.updated_src);

    if (reactJsonOptions?.onEdit) {
      reactJsonOptions.onEdit(edit);
    }
  }

  function onAdd(add: InteractionProps) {
    change(add.updated_src);

    if (reactJsonOptions?.onAdd) {
      reactJsonOptions.onAdd(add);
    }
  }

  function onDelete(del: InteractionProps) {
    change(del.updated_src);

    if (reactJsonOptions?.onDelete) {
      reactJsonOptions.onDelete(del);
    }
  }

  let src = value;

  if (jsonString) {
    src = value ? JSON.parse(value) : value;
  }

  const finalStyles: ReactJsonViewProps['style'] = {
    fontSize: '12px',
    width: '100%',
    padding: '1rem',
    borderRadius: '4px',
    ...(theme === 'light' && {
      backgroundColor: '#F9F9F9',
    }),
    ...customStyles,
  };

  return (
    <>
      <Labeled source={source} label={label ?? false} isRequired={isRequired}>
        <ReactJson
          {...defaultOptions}
          {...reactJsonOverrides}
          src={src || {}}
          onEdit={reactJsonOptions?.onEdit === false ? false : onEdit}
          onAdd={reactJsonOptions?.onAdd === false ? false : onAdd}
          onDelete={reactJsonOptions?.onDelete === false ? false : onDelete}
          quotesOnKeys={false}
          theme={theme === 'dark' ? 'ocean' : 'rjv-default'}
          style={finalStyles}
        />
      </Labeled>
      {(helperText || error) && (
        <FormHelperText error={(isTouched || isSubmitted) && !!error}>
          {error?.message ? (
            <InputHelperText error={error.message} />
          ) : (
            helperText && <InputHelperText helperText={helperText} />
          )}
        </FormHelperText>
      )}
    </>
  );
};
